import type { Category } from "../../models/Category";

/** ---- Types ---- */
export type CategoryNode = Category & { children: CategoryNode[] };
export type FlatWithLevel = Category & { level: number };

/** Shape the `/categories` endpoint might actually return: each row may (or may not) carry its own nested `children`. */
export type ApiCategoryNode = Category & { children?: ApiCategoryNode[] };

export function flattenApiResponse(items: ApiCategoryNode[]): Category[] {
    const out: Category[] = [];
    const walk = (nodes: ApiCategoryNode[], parentIdContext: number | null) => {
        for (const n of nodes) {
            const { children, ...rest } = n;
            const parentId = rest.parentId ?? parentIdContext;
            out.push({ ...rest, parentId });
            if (children && children.length) walk(children, rest.id);
        }
    };
    walk(items, null);
    return out;
}

/**
 * ---- Build a tree from flat rows ----
 * Orphans (parentId set but pointing at a row that doesn't exist — e.g. a
 * system category filtered out because "include system" is off) are
 * surfaced as roots rather than silently dropped.
 */
export function buildTree(rows: Category[]): CategoryNode[] {
    const byId = new Map<number, CategoryNode>();
    const roots: CategoryNode[] = [];

    for (const r of rows) byId.set(r.id, { ...r, children: [] });

    for (const n of byId.values()) {
        if (n.parentId == null || !byId.has(n.parentId)) {
            roots.push(n);
        } else {
            byId.get(n.parentId)!.children.push(n);
        }
    }

    const sortTree = (node: CategoryNode) => {
        node.children.sort((a, b) => a.name.localeCompare(b.name));
        node.children.forEach(sortTree);
    };
    roots.sort((a, b) => a.name.localeCompare(b.name));
    roots.forEach(sortTree);

    return roots;
}

/** ---- Flatten the tree with a 'level' depth (used by the "move" combobox fallback) ---- */
export function flattenWithLevel(nodes: CategoryNode[], level = 0): FlatWithLevel[] {
    const out: FlatWithLevel[] = [];
    for (const n of nodes) {
        out.push({ id: n.id, name: n.name, clientId: n.clientId, parentId: n.parentId, level });
        if (n.children.length) out.push(...flattenWithLevel(n.children, level + 1));
    }
    return out;
}

/** ---- Indented label (kept for the compact "move" combobox fallback) ---- */
const NBSP = " ";
const INDENT = NBSP.repeat(2);
export function formatIndentedLabel(name: string, level: number) {
    return level === 0 ? name : `${INDENT.repeat(level)}▸ ${name}`;
}

/** ---- Ancestor path for a node, root-first (powers the breadcrumb trail) ---- */
export function getPath(rows: Category[], categoryId: number | null): Category[] {
    if (categoryId == null) return [];
    const byId = new Map(rows.map((r) => [r.id, r]));
    const path: Category[] = [];
    let current = byId.get(categoryId);
    const seen = new Set<number>();
    while (current && !seen.has(current.id)) {
        path.unshift(current);
        seen.add(current.id);
        current = current.parentId == null ? undefined : byId.get(current.parentId);
    }
    return path;
}

/** ---- Count all descendants of a node (for delete warnings) ---- */
export function countDescendants(rows: Category[], categoryId: number): number {
    const children = new Map<number, Category[]>();
    for (const r of rows) {
        if (r.parentId == null) continue;
        const list = children.get(r.parentId) ?? [];
        list.push(r);
        children.set(r.parentId, list);
    }
    let count = 0;
    const stack = [...(children.get(categoryId) ?? [])];
    while (stack.length) {
        const next = stack.pop()!;
        count++;
        stack.push(...(children.get(next.id) ?? []));
    }
    return count;
}

/**
 * ---- Is `candidateId` the same node as, or a descendant of, `nodeId`? ----
 * Blocks drag-and-drop moves (and manual parent selection) that would
 * create a cycle.
 */
export function isSameOrDescendant(rows: Category[], nodeId: number, candidateId: number): boolean {
    if (nodeId === candidateId) return true;
    const byId = new Map(rows.map((r) => [r.id, r]));
    let current = byId.get(candidateId);
    const seen = new Set<number>();
    while (current && !seen.has(current.id)) {
        if (current.parentId === nodeId) return true;
        seen.add(current.id);
        current = current.parentId == null ? undefined : byId.get(current.parentId);
    }
    return false;
}

/**
 * ---- Prune the tree to nodes matching `query`, keeping ancestors of any match ----
 * Returns the pruned tree plus the set of node ids that should be
 * force-expanded to reveal matches.
 */
export function filterTree(nodes: CategoryNode[], query: string): { tree: CategoryNode[]; expandIds: Set<number> } {
    const q = query.trim().toLowerCase();
    const expandIds = new Set<number>();
    if (!q) return { tree: nodes, expandIds };

    const walk = (list: CategoryNode[]): CategoryNode[] => {
        const result: CategoryNode[] = [];
        for (const n of list) {
            const selfMatch = n.name.toLowerCase().includes(q);
            const filteredChildren = walk(n.children);
            if (selfMatch || filteredChildren.length) {
                result.push({ ...n, children: filteredChildren });
                if (filteredChildren.length) expandIds.add(n.id);
            }
        }
        return result;
    };

    return { tree: walk(nodes), expandIds };
}
